import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, UserRound } from "lucide-react";
import { leadPatch } from "@shared/schemas/leads";
import { formatGuestCount, leadGuests, parseGuestCount } from "@shared/guestCount";
import { errorMessage } from "@web/lib/api";
import { changedValues, useFormValidation } from "@web/lib/formValidation";
import { parseInput, evaluation } from "@shared/validation/issues";
import { validateLead } from "@shared/validation/leads";
import { fmtLocal, fmtPhone, fmtStamp, fullName } from "@web/lib/format";
import { DISPOSITION_LABEL, isoToLocalInput, localInputToIso, tourStatusLabel, tourStatusTone } from "@web/lib/formatLeads";
import { eventTypesQuery, leadSourcesQuery, membersQuery, venuesQuery, type Contact } from "@web/lib/queries";
import { leadApi, type LeadDetail, type Tour } from "@web/lib/queries-leads";
import { BottomSheet, Button, FormGrid, FormSection, Inline, MoneyField, Picker, Row, RowGroup, SaveBar, Stack, StatusChip, Text, TextArea, TextField, Toggle, ValidationSummary, UnsavedChanges, useToast } from "@web/ui";
import { ContactPicker } from "../sheets/ContactPicker";

type Props = { lead: LeadDetail; onTour: (t: Tour) => void; onContact: (id: string) => void };

function initial(l: LeadDetail) {
  const guests = leadGuests(l);
  return {
    venueId: l.venueId ?? "",
    eventName: l.eventName ?? "",
    eventTypeId: l.eventTypeId ?? "",
    eventTypeRaw: l.eventTypeRaw ?? "",
    eventDate: l.eventDate ?? "",
    eventDateFlexible: Boolean(l.eventDateFlexible),
    guestCount: guests ? formatGuestCount(guests.min, guests.max) : "",
    budget: l.budget as number | null,
    notes: l.notes ?? "",
    assignedMemberId: l.assignedMemberId ?? "",
    nextActionAt: isoToLocalInput(l.nextActionAt),
    nextActionNote: l.nextActionNote ?? "",
    sourceId: l.sourceId ?? "",
    sourceDetail: l.sourceDetail ?? "",
  };
}

const SOURCE_FIELDS = [
  ["sourcePage", "Page"],
  ["sourceUrl", "URL"],
  ["referrer", "Referrer"],
  ["utmSource", "utm_source"],
  ["utmMedium", "utm_medium"],
  ["utmCampaign", "utm_campaign"],
  ["utmTerm", "utm_term"],
  ["utmContent", "utm_content"],
  ["gclid", "gclid"],
  ["fbclid", "fbclid"],
  ["externalId", "External id"],
] as const;

/** Details tab: every lead field editable via PATCH, secondary contact, source + detail, read-only attribution, and the Tours card. */
export function DetailsTab({ lead, onTour, onContact }: Props) {
  const toast = useToast();
  const [form, setForm] = useState(() => initial(lead));
  const [saved, setSaved] = useState(() => initial(lead));
  const [picking, setPicking] = useState(false);
  const contactSaving = useRef(false);
  const [contactBusy, setContactBusy] = useState(false);
  const venues = useQuery(venuesQuery());
  const eventTypes = useQuery(eventTypesQuery(true));
  const sources = useQuery(leadSourcesQuery(true));
  const members = useQuery(membersQuery);
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  const memberOptions = useMemo(() => (members.data ?? []).filter((m) => m.active || m.id === lead.assignedMemberId).map((m) => ({ value: m.id, label: m.name })), [members.data, lead.assignedMemberId]);
  const toBody = (value: typeof form) => ({ ...value, venueId: value.venueId || null, eventTypeId: value.eventTypeId || null, assignedMemberId: value.assignedMemberId || null, sourceId: value.sourceId || null, eventDate: value.eventDate || null, nextActionAt: localInputToIso(value.nextActionAt), guestCount: value.guestCount.trim() || null });
  const patch = changedValues(toBody(form), toBody(saved));
  const parsed = parseInput(leadPatch, patch, { type: "lead", id: lead.id }, "lead.save");
  const guests = parseGuestCount(form.guestCount);
  const local = validateLead({ ...lead, ...(parsed.success ? parsed.data : {}), contact: undefined, secondaryContact: undefined, guestCount: guests?.min ?? null, guestCountMin: guests?.min ?? null, guestCountMax: guests?.max ?? null }, { before: lead, changedFields: Object.keys(patch), primaryContact: lead.contact, secondaryContact: lead.secondaryContact });
  const validation = useFormValidation(evaluation([...parsed.issues, ...local.issues], false));
  const { busy } = validation;
  const pending = busy || contactBusy;
  const field = (name: string) => ({ ...validation.field(name), disabled: pending });
  const edit = (change: () => void) => { if (!contactSaving.current) validation.edit(change); };
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => edit(() => setForm((f) => ({ ...f, [k]: v })));

  async function save() {
    if (contactSaving.current || !parsed.success || !validation.begin("lead.save")) return;
    try {
      await leadApi.patchLead(lead.id, parsed.data);
      setSaved(form);
      validation.clear();
      toast.success("Saved");
    } catch (e) {
      validation.reject(e);
      toast.error(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  async function setSecondary(c: Contact | null) {
    // The child's pending fence is already active here. Only the independent
    // detail-save lock applies to this callback; testing the child ref would
    // deadlock its own successful create -> attach sequence.
    if (!validation.begin("audit")) throw Error("A lead save is in progress. Try selecting the contact again after it finishes.");
    try {
      await leadApi.patchLead(lead.id, { secondaryContactId: c?.id ?? null });
      validation.clear();
      setPicking(false);
      toast.success(c ? "Secondary contact set" : "Secondary contact removed");
    } catch (e) {
      validation.reject(e);
      throw e;
    } finally { validation.finish(); }
  }

  async function removeSecondary() {
    if (contactSaving.current || busy) return;
    try { await setSecondary(null); }
    catch (e) { toast.error(errorMessage(e)); }
  }

  const attribution = SOURCE_FIELDS.filter(([k]) => lead[k]);
  return (
    <Stack gap={4}>
      <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
      <UnsavedChanges dirty={dirty} pending={pending} />
      <FormSection title="Event details">
      <Picker label="Venue" placeholder="No venue" value={form.venueId} {...field("venueId")} onChange={(v) => set("venueId", v)} options={(venues.data ?? []).map((v) => ({ value: v.id, label: v.name }))} />
      <TextField label="Event name" placeholder="e.g. Walker wedding" value={form.eventName} onChange={(e) => set("eventName", e.target.value)} {...field("eventName")} />
      <FormGrid>
        <Picker label="Event type" placeholder="—" className="flex-1" value={form.eventTypeId} onChange={(v) => set("eventTypeId", v)} options={(eventTypes.data ?? []).filter((t) => t.active || t.id === lead.eventTypeId).map((t) => ({ value: t.id, label: t.name }))} {...field("eventTypeId")} />
        <TextField label="Guests" placeholder="100–150" value={form.guestCount} {...field("guestCount")} onChange={(e) => set("guestCount", e.target.value)} />
      </FormGrid>
      {(form.eventTypeRaw || lead.eventTypeRaw) && <TextField label="Event type (as written)" value={form.eventTypeRaw} onChange={(e) => set("eventTypeRaw", e.target.value)} {...field("eventTypeRaw")} />}
      <TextField label="Event date" type="date" value={form.eventDate} {...field("eventDate")} onChange={(e) => set("eventDate", e.target.value)} />
      <Toggle label="Date is flexible" checked={form.eventDateFlexible} onChange={(v) => set("eventDateFlexible", v)} disabled={pending} />
      <MoneyField label="Budget" value={form.budget} onChange={(v) => set("budget", v)} {...field("budget")} />
      </FormSection>
      <FormSection title="Next steps">
      <Picker label="Assignee" placeholder="Unassigned" value={form.assignedMemberId} onChange={(v) => set("assignedMemberId", v)} options={memberOptions} {...field("assignedMemberId")} />
      <Stack gap={2}>
        <Text variant="headline">Next action</Text>
        <TextField label="When" type="datetime-local" value={form.nextActionAt} onChange={(e) => set("nextActionAt", e.target.value)} hint={lead.nextActionAt ? `Currently ${fmtStamp(lead.nextActionAt)}` : "Overdue actions show on Today"} {...field("nextActionAt")} />
        <TextField label="What" placeholder="e.g. Send pricing, call back" value={form.nextActionNote} onChange={(e) => set("nextActionNote", e.target.value)} {...field("nextActionNote")} />
      </Stack>
      <TextArea label="Notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} {...field("notes")} />
      </FormSection>
      <FormSection title="Source">
      <Stack gap={2}>
        <Picker label="Lead source" placeholder="—" value={form.sourceId} onChange={(v) => set("sourceId", v)} options={(sources.data ?? []).filter((s) => s.active || s.id === lead.sourceId).map((s) => ({ value: s.id, label: s.name }))} {...field("sourceId")} />
        <TextField label="Source detail" placeholder="Who referred them, which listing…" value={form.sourceDetail} onChange={(e) => set("sourceDetail", e.target.value)} {...field("sourceDetail")} />
      </Stack>
      </FormSection>
      <SaveBar><Button loading={busy} disabled={!dirty || contactBusy} onClick={save}>
        Save changes
      </Button></SaveBar>

      <Stack gap={2}>
        <Text variant="headline">Contacts</Text>
        <RowGroup>
          <Row icon={<UserRound aria-hidden size={22} />} title={fullName(lead.contact) || lead.contact.email || lead.contact.phone || "Contact"} subtitle={`Primary · ${[lead.contact.companyName, lead.contact.title, fmtPhone(lead.contact.phone), lead.contact.email].filter(Boolean).join(" · ")}`} wrap onClick={() => onContact(lead.contact.id)} />
          {lead.secondaryContact ? (
            <SecondaryRow c={lead.secondaryContact} disabled={pending} onOpen={onContact} onChange={() => edit(() => setPicking(true))} onRemove={removeSecondary} />
          ) : (
            <Row icon={<UserRound aria-hidden size={22} />} title="Add secondary contact" subtitle="Planner, partner, parent" onClick={pending ? undefined : () => edit(() => setPicking(true))} />
          )}
        </RowGroup>
      </Stack>

      <Stack gap={2}>
        <Text variant="headline">Tours</Text>
        {lead.tours.length === 0 ? (
          <Text variant="callout" tone="secondary">
            No tours yet — use Schedule tour below.
          </Text>
        ) : (
          <RowGroup>
            {lead.tours.map((t) => (
              <Row
                key={t.id}
                icon={<CalendarClock aria-hidden size={22} />}
                title={fmtLocal(t.scheduledAt)}
                subtitle={`${t.durationMin} min${t.disposition ? ` · ${DISPOSITION_LABEL[t.disposition] ?? t.disposition}` : ""}${t.notes ? ` · ${t.notes}` : ""}`}
                trailing={<StatusChip tone={tourStatusTone(t.status)}>{tourStatusLabel(t.status)}</StatusChip>}
                onClick={t.status === "scheduled" ? () => onTour(t) : undefined}
              />
            ))}
          </RowGroup>
        )}
      </Stack>

      {attribution.length > 0 && (
        <Stack gap={2}>
          <Text variant="headline">Attribution</Text>
          <RowGroup>
            {attribution.map(([k, label]) => (
              <Row key={k} title={label} subtitle={String(lead[k])} wrap />
            ))}
          </RowGroup>
        </Stack>
      )}
      <Text variant="caption" tone="tertiary" align="center">
        Created {fmtStamp(lead.createdAt)}
        {lead.sourceName ? ` via ${lead.sourceName}` : ""}
      </Text>

      <BottomSheet open={picking} onClose={() => { if (!contactSaving.current && !busy) setPicking(false); }} title="Secondary contact">
        <ContactPicker value={null} onPendingChange={value => { contactSaving.current = value; setContactBusy(value); }} onChange={(c) => { if (c) return setSecondary(c); }} />
      </BottomSheet>
    </Stack>
  );
}

function SecondaryRow({ c, onOpen, onChange, onRemove, disabled }: { c: Contact; onOpen: (id: string) => void; onChange: () => void; onRemove: () => void; disabled: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Row icon={<UserRound aria-hidden size={22} />} title={fullName(c) || c.email || c.phone || "Contact"} subtitle={`Secondary · ${[c.companyName, c.title, fmtPhone(c.phone), c.email].filter(Boolean).join(" · ")}`} wrap onClick={disabled ? undefined : () => setOpen(true)} />
      <BottomSheet open={open} onClose={() => setOpen(false)} title="Secondary contact">
        <RowGroup>
          <Row title="View contact" subtitle="Details, consent, bookings" onClick={() => (setOpen(false), onOpen(c.id))} />
          <Row title="Change contact" onClick={() => (setOpen(false), onChange())} />
          <Row title="Remove secondary contact" onClick={() => (setOpen(false), onRemove())} />
        </RowGroup>
      </BottomSheet>
    </>
  );
}
